import React, { forwardRef, useEffect, useState, useCallback, ChangeEvent, useRef } from 'react';

import { escapeRegExp, find, isEmpty } from 'lodash';
import { Dropdown, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { CustomDateFilter } from 'common/filters/filter-bar/custom-date-filter/CustomDateFilter';
import { DataType, FilterDefinition, FilterValue } from 'common/filters/Filters.types';
import { Period } from 'core/util/DateRange';
import 'common/filters/filter-bar/single-select-filter/SingleSelectFilter.scss';

interface Props {
    filter: FilterDefinition;
    focusClass?: string;
    searchValue?: string;
    onChange: (filter: FilterDefinition) => void;
    showSearch?: boolean;
    isOpen?: boolean;
}

export const SingleSelectFilter = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const isCustomDate = useCallback(
        (filterValue?: FilterValue): boolean => {
            return props.filter.dataType === DataType.Date && filterValue?.value === Period.Custom;
        },
        [props.filter.dataType],
    );
    const { t } = useTranslation(['common/filters']);
    const selectedFilter = find(props.filter.values, 'selected');
    const [selectedValue, setSelectedValue] = useState(selectedFilter);
    const [isCustomDateView, setCustomDateView] = useState(isCustomDate(selectedValue));
    const [search, setSearch] = useState('');
    const [dateFilterValues, setDateFilterValues] = useState<Record<string, any[]>>({});

    useEffect(() => {
        if (props.filter.dataType === DataType.Date) {
            const groupedDateFacetOptions = props.filter.values.reduce((acc, option) => {
                let key = '';

                if (option.group === 'fiscal') {
                    key = 'fiscal';
                } else if (option.group === 'gregorian') {
                    key = 'gregorian';
                }

                acc[key] = acc[key] || [];
                if (!acc[key]) {
                    acc[key] = [];
                }

                acc[key].push(option);
                return acc;
            }, {} as Record<string, any[]>);

            setDateFilterValues(groupedDateFacetOptions);
        }
    }, [props.filter.values, props.filter.dataType]);

    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const selectFilterValue = (filterValue: FilterValue): void => {
        if (isCustomDate(filterValue) && !isCustomDateView) {
            setCustomDateView(true);
        } else {
            const nextValues = props.filter.values.map((value) => ({
                ...value,
                selected: value.value === filterValue.value,
            }));

            props.onChange({
                ...props.filter,
                values: nextValues,
            });

            setSelectedValue({ ...filterValue });

            setCustomDateView(false);
        }
    };

    useEffect(() => {
        setSelectedValue(selectedFilter);
        setCustomDateView(isCustomDate(selectedFilter));
        setSearch('');
    }, [selectedFilter, isCustomDate]);

    useEffect(() => {
        if (!props.isOpen) {
            setCustomDateView(false);
        }
    }, [props.isOpen]);

    const filterValues = (filterValue: FilterValue): boolean => {
        let isMatch = true;
        const searchValue = props.searchValue || search;

        if (!isEmpty(searchValue)) {
            const pattern = new RegExp(escapeRegExp(searchValue), 'i');

            isMatch = pattern.test(filterValue.label) || pattern.test(filterValue.value);
        }

        return isMatch;
    };

    const cancelCustomDate = (): void => {
        setCustomDateView(false);
    };

    const applyCustomDate = (filterValue: FilterValue): void => {
        selectFilterValue(filterValue);
    };

    const getRenderedOptions = useCallback((): HTMLElement[] => {
        if (!menuRef.current) return [];

        return Array.from(menuRef.current.querySelectorAll<HTMLElement>('[data-filter-option="true"]')).filter(
            (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
        );
    }, []);

    const handleOptionArrowNavigation = useCallback(
        (e: React.KeyboardEvent<HTMLElement>, currentEl: HTMLElement): void => {
            const options = getRenderedOptions();
            const currentIndex = options.indexOf(currentEl);

            if (currentIndex === -1) return;

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();

                if (currentIndex === 0) {
                    searchInputRef.current?.focus();
                } else {
                    options[currentIndex - 1]?.focus();
                }
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();

                if (currentIndex === options.length - 1) {
                    searchInputRef.current?.focus();
                } else {
                    options[currentIndex + 1]?.focus();
                }
            }
        },
        [getRenderedOptions],
    );

    const setMenuRef = useCallback(
        (node: HTMLDivElement | null) => {
            menuRef.current = node;

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                const objectRef = ref as React.MutableRefObject<HTMLDivElement | null>;

                objectRef.current = node;
            }
        },
        [ref],
    );

    const visibleOptionCount =
        props.filter.dataType === DataType.Date
            ? Object.values(dateFilterValues).reduce((count, items) => {
                  return count + items.filter((filterValue) => filterValues(filterValue)).length;
              }, 0)
            : props.filter.values.filter((filterValue) => filterValues(filterValue)).length;

    return isCustomDateView ? (
        <Dropdown.Menu
            filter={props.filter}
            onCancel={cancelCustomDate}
            onChange={applyCustomDate}
            as={CustomDateFilter}
        />
    ) : (
        <Dropdown.Menu
            className="r4e-single-select-filter"
            data-attr-name={props.filter.name}
            data-filter-menu="true"
            role="radiogroup"
            aria-label={props.filter.label}
            id={`${props.filter.name}-options`}
            ref={setMenuRef}
        >
            {props.showSearch && (
                <div className="dropdown-header">
                    <div className="search-wrapper">
                        <i className="icon-search" />
                        <label className="sr-only" htmlFor={`${props.filter.name}-search`}>
                            {t('SEARCH')}
                        </label>

                        <FormControl
                            id={`${props.filter.name}-search`}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key !== 'ArrowDown') return;

                                e.preventDefault();
                                const options = getRenderedOptions();

                                options[0]?.focus();
                            }}
                            ref={searchInputRef}
                            value={search}
                            autoFocus
                            onChange={(e: ChangeEvent<HTMLInputElement>): void => setSearch(e.target.value)}
                            aria-label={t('FILTER_ITEMS_COUNT', { visibleOptionCount })}
                            aria-controls={`${props.filter.name}-options`}
                        />
                    </div>
                </div>
            )}
            {props.children}
            {props.filter.dataType === DataType.Date &&
                Object.entries(dateFilterValues).map(([groupName, items], index, arr) => (
                    <React.Fragment key={groupName}>
                        {groupName && <Dropdown.Header> {t(groupName)} </Dropdown.Header>}
                        {groupName && index <= arr.length - 1 && <Dropdown.Divider />}

                        {items
                            .filter((filterValue) => filterValues(filterValue))
                            .map((filterValue, index) => {
                                const isCustomOption = isCustomDate(filterValue);
                                const itemClassName = index === 0 && props.focusClass ? props.focusClass : '';

                                if (isCustomOption) {
                                    return (
                                        <button
                                            key={filterValue.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={!!filterValue.selected}
                                            className={`dropdown-item ${itemClassName}`}
                                            data-filter-option="true"
                                            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                                                handleOptionArrowNavigation(e, e.currentTarget);
                                            }}
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setCustomDateView(true);
                                            }}
                                        >
                                            {filterValue.selected && <i className="icon-check" aria-hidden="true" />}
                                            {filterValue.label}
                                        </button>
                                    );
                                }

                                return (
                                    <Dropdown.Item
                                        as="button"
                                        type="button"
                                        role="radio"
                                        aria-checked={!!filterValue.selected}
                                        data-filter-option="true"
                                        key={filterValue.value}
                                        onKeyDownCapture={(e: React.KeyboardEvent<HTMLElement>) => {
                                            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                                            handleOptionArrowNavigation(e, e.currentTarget);
                                        }}
                                        className={index === 0 && props.focusClass ? props.focusClass : ''}
                                        onClick={(): void => selectFilterValue(filterValue)}
                                    >
                                        {filterValue.selected && <i className="icon-check" aria-hidden="true" />}
                                        {filterValue.label}
                                    </Dropdown.Item>
                                );
                            })}
                    </React.Fragment>
                ))}

            {props.filter.dataType !== DataType.Date &&
                props.filter.values
                    .filter((filterValue) => filterValues(filterValue))
                    .map((filterValue, index) => (
                        <Dropdown.Item
                            as="button"
                            type="button"
                            role="radio"
                            data-filter-option="true"
                            aria-checked={!!filterValue.selected}
                            key={filterValue.value}
                            className={index === 0 && props.focusClass ? props.focusClass : ''}
                            onClick={(): void => selectFilterValue(filterValue)}
                            onKeyDownCapture={(e: React.KeyboardEvent<HTMLElement>) => {
                                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                                handleOptionArrowNavigation(e, e.currentTarget);
                            }}
                        >
                            {filterValue.selected && <i className="icon-check" aria-hidden="true" />}
                            {filterValue.label}
                        </Dropdown.Item>
                    ))}
        </Dropdown.Menu>
    );
});
